import React, { useEffect, useState } from 'react';
import { adminService, authService } from '../../services/api';
import { AdminUserSummary } from '../../types';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Box, Chip, IconButton, Tooltip, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Snackbar, Alert
} from '@mui/material';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';
import DeleteIcon from '@mui/icons-material/Delete';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';

const AdminUsersList: React.FC = () => {
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; user: AdminUserSummary | null }>({ open: false, user: null });
  const [deactivateDialog, setDeactivateDialog] = useState<{ open: boolean; user: AdminUserSummary | null }>({ open: false, user: null });
  const [activateDialog, setActivateDialog] = useState<{ open: boolean; user: AdminUserSummary | null }>({ open: false, user: null });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });
  const [actionLoading, setActionLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await authService.logout(refreshToken);
      }
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
      localStorage.removeItem('dashboardData');
      localStorage.removeItem('authUser');
      navigate('/login');
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteDialog.user) return;
    
    setActionLoading(true);
    try {
      await adminService.deleteUser(deleteDialog.user.id);
      setUsers(users.filter(user => user.id !== deleteDialog.user!.id));
      setDeleteDialog({ open: false, user: null });
      setSnackbar({ open: true, message: `Usuario ${deleteDialog.user.full_name} eliminado exitosamente`, severity: 'success' });
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      setSnackbar({ open: true, message: 'Error al eliminar el usuario', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeactivateUser = async () => {
    if (!deactivateDialog.user) return;
    
    setActionLoading(true);
    try {
      await adminService.deactivateUser(deactivateDialog.user.id);
      setUsers(users.map(user => 
        user.id === deactivateDialog.user!.id 
          ? { ...user, is_active: false }
          : user
      ));
      setDeactivateDialog({ open: false, user: null });
      setSnackbar({ open: true, message: `Usuario ${deactivateDialog.user.full_name} desactivado exitosamente`, severity: 'success' });
    } catch (error) {
      console.error('Error al desactivar usuario:', error);
      setSnackbar({ open: true, message: 'Error al desactivar el usuario', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleActivateUser = async () => {
    if (!activateDialog.user) return;
    
    setActionLoading(true);
    try {
      await adminService.activateUser(activateDialog.user.id);
      setUsers(users.map(user => 
        user.id === activateDialog.user!.id 
          ? { ...user, is_active: true }
          : user
      ));
      setActivateDialog({ open: false, user: null });
      setSnackbar({ open: true, message: `Usuario ${activateDialog.user.full_name} reactivado exitosamente`, severity: 'success' });
    } catch (error) {
      console.error('Error al reactivar usuario:', error);
      setSnackbar({ open: true, message: 'Error al reactivar el usuario', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const data = await adminService.getAdminUsers();
        setUsers(data);
      } catch (err) {
        setError('Error al cargar los usuarios');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" minHeight="40vh"><CircularProgress /></Box>;
  }

  if (error) {
    return <Typography color="error">{error}</Typography>;
  }

  return (
    <Box p={3}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/dashboard')}>
          Volver al Panel
        </Button>
        <Button
          variant="outlined"
          color="error"
          startIcon={<LogoutIcon />}
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            px: 3,
            py: 1.5,
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2,
              backgroundColor: '#ffebee'
            }
          }}
        >
          Cerrar Sesión
        </Button>
      </Box>
      <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>Usuarios registrados</Typography>
      <TableContainer component={Paper} sx={{ boxShadow: 6, borderRadius: 4 }}>
        <Table size="small" sx={{ minWidth: 400 }}>
          <TableHead>
            <TableRow sx={{ background: 'linear-gradient(90deg, #7C3AED 0%, #22C55E 100%)' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 700, position: 'sticky', left: 0, background: 'inherit', zIndex: 1 }}>Nombre</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Email</TableCell>
              <TableCell align="center" sx={{ color: '#fff', fontWeight: 700 }}>Niños</TableCell>
              <TableCell align="center" sx={{ color: '#fff', fontWeight: 700 }}>Estado</TableCell>
              <TableCell align="center" sx={{ color: '#fff', fontWeight: 700 }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} hover sx={{ transition: 'background 0.2s', '&:hover': { background: '#f3e8ff' } }}>
                <TableCell sx={{ fontWeight: 600 }}>{user.full_name}</TableCell>
                <TableCell>{user.email}</TableCell>
                <TableCell align="center">
                  <Chip label={user.children_count} color="secondary" variant="outlined" size="small" sx={{ fontWeight: 700 }} />
                </TableCell>
                <TableCell align="center">
                  {user.is_active ? <Chip label="Activo" sx={{ bgcolor: '#22C55E', color: '#fff', fontWeight: 700 }} size="small" /> : <Chip label="Inactivo" color="default" size="small" />}
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                    <Tooltip title="Ver detalle">
                      <IconButton color="primary" onClick={() => navigate(`/admin/users/${user.id}`)}>
                        <PersonSearchIcon />
                      </IconButton>
                    </Tooltip>
                    
                    {user.is_active ? (
                      <Tooltip title="Desactivar usuario">
                        <IconButton 
                          color="warning" 
                          onClick={() => setDeactivateDialog({ open: true, user })}
                          disabled={actionLoading}
                        >
                          <BlockIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Reactivar usuario">
                        <IconButton 
                          color="success" 
                          onClick={() => setActivateDialog({ open: true, user })}
                          disabled={actionLoading}
                        >
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    <Tooltip title="Eliminar usuario permanentemente">
                      <IconButton 
                        color="error" 
                        onClick={() => setDeleteDialog({ open: true, user })}
                        disabled={actionLoading}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo de confirmación para eliminar usuario */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, user: null })}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que quieres eliminar permanentemente al usuario <strong>{deleteDialog.user?.full_name}</strong>?
            <br />
            <br />
            <strong>⚠️ Esta acción no se puede deshacer.</strong> Se eliminarán todos los datos del usuario incluyendo:
            <br />
            • Actividades y experiencias
            <br />
            • Datos de los niños
            <br />
            • Estrellas y Tfiats
            <br />
            • Transacciones
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, user: null })} disabled={actionLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDeleteUser} 
            color="error" 
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {actionLoading ? 'Eliminando...' : 'Eliminar permanentemente'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para desactivar usuario */}
      <Dialog open={deactivateDialog.open} onClose={() => setDeactivateDialog({ open: false, user: null })}>
        <DialogTitle>Confirmar desactivación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que quieres desactivar al usuario <strong>{deactivateDialog.user?.full_name}</strong>?
            <br />
            <br />
            El usuario no podrá acceder al sistema pero sus datos se mantendrán intactos.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDialog({ open: false, user: null })} disabled={actionLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleDeactivateUser} 
            color="warning" 
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <BlockIcon />}
          >
            {actionLoading ? 'Desactivando...' : 'Desactivar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de confirmación para reactivar usuario */}
      <Dialog open={activateDialog.open} onClose={() => setActivateDialog({ open: false, user: null })}>
        <DialogTitle>Confirmar reactivación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que quieres reactivar al usuario <strong>{activateDialog.user?.full_name}</strong>?
            <br />
            <br />
            El usuario podrá acceder nuevamente al sistema.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setActivateDialog({ open: false, user: null })} disabled={actionLoading}>
            Cancelar
          </Button>
          <Button 
            onClick={handleActivateUser} 
            color="success" 
            variant="contained"
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
          >
            {actionLoading ? 'Reactivando...' : 'Reactivar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para notificaciones */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminUsersList; 